import { useMemo } from "react";

import { typeForPage } from "src/components/commandbar/command-index";
import type { CommandAction } from "src/components/commandbar/types";
import { useDeepStable } from "src/components/commandbar/use-command-bar-context";
import { useRegisterCommandActions } from "src/components/commandbar/use-register-command-actions";
import {
  type ReachableEntry,
  useReachableNav,
} from "src/components/layout/nav/use-reachable-nav";
import { PROJECTS_PATHS } from "src/pages/projects/constants";
import { useNavigate } from "src/router";

const LIBRARY = "library";
// The product nav's Content row opens the Library's New view.
const LIBRARY_HREFS = new Set<string>([
  PROJECTS_PATHS.library,
  PROJECTS_PATHS.libraryNew,
]);
// Above Switch workspace (5) and Docs (0).
const SECTION = { name: "Workspace", priority: 10 };

const KEYWORDS: Record<string, string> = {
  // The product nav calls the Library "Content", and canvases are among its files.
  [LIBRARY]: "library content canvas canvases files assets",
  "nav.Identity Resolution": "idr",
  "nav.AI Decisioning": "aid",
};

// The ways to create each type, which a search reaches under that type's command.
const CREATE_COMMANDS: Record<string, Array<{ name: string; href: string }>> = {
  syncs: [{ name: "New sync", href: "/syncs/new" }],
  models: [{ name: "New model", href: "/models/new" }],
  audiences: [{ name: "New audience", href: "/audiences/new" }],
  sources: [{ name: "Create source", href: "/sources/new" }],
  destinations: [{ name: "Create destination", href: "/destinations/new" }],
};

// A link to a type's page takes the type as its id, so its command stands for the type's scope.
const commandId = (href: string, fallback: string) =>
  (LIBRARY_HREFS.has(href) ? LIBRARY : typeForPage(href)) ?? fallback;
// Content itself stands for the Library, so Tab on it lists its rail rows above its files.
const sectionId = (entry: ReachableEntry) =>
  LIBRARY_HREFS.has(entry.href) ? LIBRARY : `nav.${entry.key}`;
const linkCommandId = (href: string, fallback: string) =>
  LIBRARY_HREFS.has(href) ? fallback : commandId(href, fallback);

/** The palette's commands for the nav's entries, in the nav's order, with each section's links under it. */
export const buildNavCommands = (
  entries: ReachableEntry[],
  navigate: (href: string) => void,
): CommandAction[] => {
  const seen = new Set<string>();
  const unique = (action: CommandAction) => {
    if (seen.has(action.id)) return false;
    seen.add(action.id);
    return true;
  };
  return entries
    .flatMap((entry, index): CommandAction[] => {
      // A section shares its first link's page, which that link's command stands for.
      const id = entry.links.length
        ? sectionId(entry)
        : commandId(entry.href, `nav.${entry.key}`);
      const links = entry.links.map(
        (link): CommandAction => ({
          id: linkCommandId(link.href, `nav.${entry.key}.${link.href}`),
          parent: id,
          name: link.label,
          keywords: KEYWORDS[linkCommandId(link.href, "")],
          iconComponent: link.icon,
          // The Library row ranks above Content for "library", so Tab on it searches the files too.
          searches: link.href === PROJECTS_PATHS.library ? LIBRARY : undefined,
          perform: () => navigate(link.href),
        }),
      );
      return [
        {
          id,
          section: SECTION,
          priority: entries.length - index,
          name: entry.label,
          keywords: KEYWORDS[id],
          iconComponent: entry.icon,
          // A section opens its links; a page opens itself.
          perform: links.length ? undefined : () => navigate(entry.href),
        },
        ...links,
      ];
    })
    .filter(unique)
    .flatMap((action) => [
      action,
      ...(CREATE_COMMANDS[action.id] ?? []).map(
        ({ name, href }): CommandAction => ({
          id: `${action.id}.new`,
          parent: action.id,
          name,
          keywords: `${action.id} new add create`,
          perform: () => navigate(href),
        }),
      ),
    ]);
};

/** The nav the viewer is on, as the palette's root commands, once its gates have loaded so one nav never swaps for the other. */
const useNavCommands = () => {
  const { entries, isLoading } = useReachableNav();
  // The entries are new every render; register again only when what they list changes.
  const stableEntries = useDeepStable(isLoading ? [] : entries);
  const navigate = useNavigate();
  const actions = useMemo(
    () => buildNavCommands(stableEntries, navigate),
    [stableEntries, navigate],
  );
  useRegisterCommandActions(actions, [actions]);
};

// Mounted only for a viewer in a workspace, since the nav's gates read the workspace.
export const NavCommands = () => {
  useNavCommands();
  return null;
};
